import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { EvidenceScenario } from '../models/evidence-scenario';

/**
 * Reads the evidence-scenario catalogue ("VPOD Type"), which is how an attachment's stored scenario
 * CODE becomes a human-readable tile label. Attachment reads carry the code and never the label, so
 * this endpoint is the only label source.
 *
 * It is a small, tenant-shared reference table requested by every file list on every gated-on screen,
 * so it is cached rather than re-fetched per view.
 */
@Injectable({
	providedIn: 'root'
})
export class EvidenceScenarioService {
	/**
	 * The in-flight or cached request, shared by every file list on screen: four host components can
	 * render an attachment list, and re-opening a tracking modal must not re-issue the request.
	 */
	private _active$: Observable<EvidenceScenario[]>;

	/**
	 * When the cached catalogue arrived, for the lifespan check. 0 = nothing cached. Also stamped at
	 * request issue so an in-flight request is not expired out from under its subscribers. A request
	 * still unresolved a full lifespan after issue is dropped by the next caller and re-issued, which
	 * costs one duplicate GET in that pathological case.
	 */
	private _cachedAt = 0;

	/**
	 * Matches the backend's own cache lifespan, so a newly added scenario becomes visible within one
	 * window rather than only after a reload.
	 *
	 * Deliberately not shareReplay's windowTime: with a completed HttpClient source, an expired replay
	 * window emits completion WITHOUT a value, leaving a late subscriber stuck in its loading state.
	 */
	private static readonly CACHE_LIFESPAN_MS = 300000;

	constructor(private _http: HttpClient) {
	}

	/**
	 * @api {get} /v2/EvidenceScenarios Get the active evidence-scenario catalogue
	 * @apiName getActiveEvidenceScenarios
	 * @apiGroup EvidenceScenarios
	 * @apiVersion 20.0.0
	 */
	getActive(): Observable<EvidenceScenario[]> {
		if (this._active$ && Date.now() - this._cachedAt >= EvidenceScenarioService.CACHE_LIFESPAN_MS) {
			this._active$ = undefined;
		}
		if (!this._active$) {
			this._cachedAt = Date.now();
			const request$: Observable<EvidenceScenario[]> = this._http
				.get<EvidenceScenario[]>(`${environment.apiUrl}/v2/EvidenceScenarios`)
				.pipe(
					// The endpoint already serves active-only; the filter makes this method's name true
					// independently of the backend, so a retired entry can never supply a label.
					map((scenarios) => (scenarios || []).filter((scenario) => scenario && scenario.isActive)),
					tap({
						// Restart the lifespan from the moment the data actually ARRIVED, so the window
						// above means what it says rather than "300s minus however long the request took".
						next: () => {
							if (this._active$ === request$) {
								this._cachedAt = Date.now();
							}
						},
						// Drop the cached observable on failure so the next subscriber re-fetches;
						// shareReplay would otherwise replay the error for the rest of the session.
						error: () => {
							if (this._active$ === request$) {
								this._active$ = undefined;
								this._cachedAt = 0;
							}
						}
					}),
					// Both writes above are identity-guarded so a superseded request can never touch a
					// newer cache entry.
					shareReplay({bufferSize: 1, refCount: false})
				);
			this._active$ = request$;
		}
		return this._active$;
	}
}
